import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Switch } from '../components/ui/Switch';
import { MockBadge } from '../components/MockBadge';
import { WarmupSheet } from '../components/WarmupSheet';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatSendPrice } from '../lib/format';
import { apiErrorMessage } from './ListerPage';
import type { SendCostSetting, Sender, SenderDomain, SenderDomainStatus } from '../types/api';

const DOMAIN_STATUS: Record<SenderDomainStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  failed: 'Failed',
};

function DomainsSection({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['sender-domains'],
    queryFn: () => apiFetch<{ items: SenderDomain[] }>('/sender-domains'),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['sender-domains'] });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<SenderDomain>('/sender-domains', { method: 'POST', body: { domain } }),
    onSuccess: () => {
      setDomain('');
      invalidate();
    },
  });
  const verifyMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<SenderDomain>(`/sender-domains/${id}/verify`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/sender-domains/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const error =
    apiErrorMessage(createMutation.error, 'The domain could not be added.') ??
    apiErrorMessage(verifyMutation.error, 'The domain could not be marked as verified.') ??
    apiErrorMessage(deleteMutation.error, 'The domain could not be removed.');
  const items = data?.items ?? [];

  return (
    <section aria-labelledby="domains-heading" className="flex flex-col gap-3 max-w-[760px]">
      <h2 id="domains-heading" className="text-base font-semibold m-0">
        Domains
      </h2>
      <p className="m-0 text-sm text-muted">
        A sender address must be on a verified domain. Until the Amazon SES setup is in place, an
        administrator marks the domain as verified here once SPF and DKIM are set up. This is
        logged.
      </p>
      {isAdmin ? (
        <form
          className="flex gap-2 items-end max-sm:flex-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5 flex-grow">
            <Label htmlFor="domain-name">New domain</Label>
            <Input
              id="domain-name"
              placeholder="example.com"
              required
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={domain.trim() === '' || createMutation.isPending}>
            Add domain
          </Button>
        </form>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {isLoading ? <div className="text-muted">Loading domains…</div> : null}
      {isError ? <Alert>Domains could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <p className="m-0 text-sm text-muted">There are no domains yet.</p>
      ) : null}
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {items.map((d) => (
          <li key={d.id}>
            <Card className="p-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium">{d.domain}</span>
              <span className={d.status === 'verified' ? 'text-success-text' : 'text-muted'}>
                {DOMAIN_STATUS[d.status]}
                {d.verificationMethod === 'manual' ? ' (manual)' : ''}
                {d.verificationMethod === 'ses' ? ' (SES)' : ''}
              </span>
              <span className="text-xs text-muted">{d.senderCount} senders</span>
              {isAdmin ? (
                <div className="ml-auto flex flex-wrap justify-end gap-2">
                  {d.status !== 'verified' ? (
                    <Button
                      variant="secondary"
                      aria-label={`Mark ${d.domain} as verified`}
                      disabled={verifyMutation.isPending}
                      onClick={() => verifyMutation.mutate(d.id)}
                    >
                      Mark as verified
                    </Button>
                  ) : null}
                  <Button
                    variant="secondary"
                    aria-label={`Remove ${d.domain}`}
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(d.id)}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </Card>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SendersSection({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [warmupSenderId, setWarmupSenderId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['senders', { showArchived }],
    queryFn: () =>
      apiFetch<{ items: Sender[] }>(`/senders${showArchived ? '?includeArchived=1' : ''}`),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['senders'] });
    void queryClient.invalidateQueries({ queryKey: ['sender-domains'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<Sender>('/senders', {
        method: 'POST',
        body: { fromName, fromEmail, replyToEmail: replyToEmail || null },
      }),
    onSuccess: (sender) => {
      setFromName('');
      setFromEmail('');
      setReplyToEmail('');
      setNotice(`${sender.fromName} <${sender.fromEmail}> was created.`);
      invalidate();
    },
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<Sender>(`/senders/${id}`, { method: 'PATCH', body: { archived } }),
    onSuccess: (sender) => {
      setNotice(
        sender.archived ? `${sender.fromEmail} was archived.` : `${sender.fromEmail} was restored.`,
      );
      invalidate();
    },
  });
  const trackingMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch<Sender>(`/senders/${id}`, {
        method: 'PATCH',
        body: { openTrackingEnabled: enabled },
      }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (sender: Sender) =>
      apiFetch<{ result: 'deleted' | 'archived' }>(`/senders/${sender.id}`, { method: 'DELETE' }),
    onSuccess: (res, sender) => {
      setNotice(
        res.result === 'deleted'
          ? `${sender.fromEmail} was deleted.`
          : `${sender.fromEmail} is used by a campaign and was archived instead.`,
      );
      invalidate();
    },
  });

  const createError = apiErrorMessage(createMutation.error, 'The sender could not be created.');
  const rowError =
    apiErrorMessage(archiveMutation.error, 'The sender could not be changed.') ??
    apiErrorMessage(trackingMutation.error, 'The sender could not be changed.') ??
    apiErrorMessage(deleteMutation.error, 'The sender could not be deleted.');
  const items = data?.items ?? [];

  return (
    <section aria-labelledby="senders-heading" className="flex flex-col gap-3 max-w-[760px]">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 id="senders-heading" className="text-base font-semibold m-0">
          Senders
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived senders
        </label>
      </div>
      {isAdmin ? (
        <Card className="p-4">
          <form
            className="grid grid-cols-3 max-sm:grid-cols-1 gap-3 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sender-name">Sender name</Label>
              <Input
                id="sender-name"
                required
                maxLength={100}
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sender-email">Sender address</Label>
              <Input
                id="sender-email"
                type="email"
                required
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sender-reply">Reply-to (optional)</Label>
              <Input
                id="sender-reply"
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              className="col-span-3 max-sm:col-span-1 justify-self-end max-sm:justify-self-stretch"
              disabled={!fromName.trim() || !fromEmail.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create sender'}
            </Button>
          </form>
          {createError ? <Alert className="mt-3">{createError}</Alert> : null}
        </Card>
      ) : null}
      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}
      {rowError ? <Alert>{rowError}</Alert> : null}
      {isLoading ? <div className="text-muted">Loading senders…</div> : null}
      {isError ? <Alert>Senders could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <p className="m-0 text-sm text-muted">There are no senders yet.</p>
      ) : null}
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {items.map((s) => (
          <li key={s.id}>
            <Card className="p-3 flex flex-wrap items-center gap-3 text-sm">
              <div>
                <div className="font-medium">
                  {s.fromName} &lt;{s.fromEmail}&gt;
                  {s.archived ? <span className="ml-2 text-xs text-muted">(archived)</span> : null}
                </div>
                <div className="text-xs text-muted">
                  {s.replyToEmail ? `Reply to ${s.replyToEmail} · ` : ''}
                  {s.campaignCount} campaigns · created {formatDate(s.createdAt)}
                </div>
                <div className="mt-2 flex items-start gap-2">
                  <Switch
                    checked={s.openTrackingEnabled}
                    label={`Track opens for ${s.fromEmail}`}
                    disabled={!isAdmin || trackingMutation.isPending}
                    onChange={() =>
                      trackingMutation.mutate({ id: s.id, enabled: !s.openTrackingEnabled })
                    }
                  />
                  <div className="max-w-[420px]">
                    <div className="text-sm font-medium">Track opens</div>
                    <div className="text-xs text-muted">
                      Adds a tiny invisible image to each email to count opens. Opens from bots and
                      Apple Mail Privacy Protection are filtered out, so the open rate is an
                      estimate.
                    </div>
                  </div>
                </div>
              </div>
              <div className="ml-auto flex flex-wrap justify-end gap-2">
                <Button
                  variant="secondary"
                  aria-label={`Warm-up for ${s.fromEmail}`}
                  onClick={() => setWarmupSenderId(s.id)}
                >
                  Warm-up
                </Button>
                {isAdmin ? (
                  <>
                    <Button
                      variant="secondary"
                      aria-label={`${s.archived ? 'Restore' : 'Archive'} ${s.fromEmail}`}
                      disabled={archiveMutation.isPending}
                      onClick={() => archiveMutation.mutate({ id: s.id, archived: !s.archived })}
                    >
                      {s.archived ? 'Restore' : 'Archive'}
                    </Button>
                    <Button
                      variant="secondary"
                      aria-label={`Delete ${s.fromEmail}`}
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(s)}
                    >
                      Delete
                    </Button>
                  </>
                ) : null}
              </div>
            </Card>
          </li>
        ))}
      </ul>
      {warmupSenderId ? (
        <WarmupSheet
          senderId={warmupSenderId}
          senderLabel={items.find((s) => s.id === warmupSenderId)?.fromEmail ?? warmupSenderId}
          isAdmin={isAdmin}
          open={warmupSenderId !== null}
          onOpenChange={(open) => {
            if (!open) setWarmupSenderId(null);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * The price of 1,000 emails handed to Amazon SES, in the reporting currency
 * (26 Oct 2026). It prices the sending cost and ROI on the Overview and in a
 * campaign's statistics. Admins change it (audit-logged); others see it.
 */
function SendingCostSection({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'sending-cost'],
    queryFn: () => apiFetch<SendCostSetting>('/settings/sending-cost'),
  });
  // `null` until the admin edits, so the field shows the saved value.
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const value = draft ?? (data ? String(data.costPerThousand) : '');
  const parsed = value.trim() === '' ? NaN : Number(value);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;

  const saveMutation = useMutation({
    mutationFn: (costPerThousand: number) =>
      apiFetch<SendCostSetting>('/settings/sending-cost', {
        method: 'PUT',
        body: { costPerThousand },
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['settings', 'sending-cost'], next);
      // Cost and ROI everywhere use the new price.
      void queryClient.invalidateQueries({ queryKey: ['stats'] });
      setDraft(null);
      setSaved(true);
    },
  });
  const error = apiErrorMessage(saveMutation.error, 'The sending cost could not be saved.');
  const currency = data?.currency ?? 'EUR';

  return (
    <section aria-labelledby="sending-cost-heading" className="flex flex-col gap-3 max-w-[760px]">
      <h2 id="sending-cost-heading" className="text-base font-semibold m-0">
        Sending cost
      </h2>
      <p className="m-0 text-sm text-muted">
        Amazon SES list price is $0.10 per 1,000 emails. Enter the amount in your currency. The SES
        free tier is not deducted.
      </p>
      {isLoading ? <div className="text-muted">Loading sending cost…</div> : null}
      {isError ? <Alert>The sending cost could not be loaded.</Alert> : null}
      {data && isAdmin ? (
        <form
          className="flex gap-2 items-end max-sm:flex-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) saveMutation.mutate(parsed);
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="send-cost">Cost per 1,000 emails ({currency})</Label>
            <Input
              id="send-cost"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step="any"
              className="w-[160px] max-sm:w-full"
              value={value}
              aria-invalid={!valid}
              onChange={(e) => {
                setDraft(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          <Button type="submit" disabled={!valid || draft === null || saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      ) : null}
      {data && !isAdmin ? (
        <Card className="p-3 text-sm">
          Cost per 1,000 emails:{' '}
          <span className="font-medium">{formatSendPrice(data.costPerThousand, currency)}</span>
        </Card>
      ) : null}
      {data && isAdmin && !valid ? (
        <p className="m-0 text-sm text-danger-text">Enter an amount from 0 to 100.</p>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {saved ? (
        <div role="status" className="text-success-text text-sm">
          Sending cost saved.
        </div>
      ) : null}
    </section>
  );
}

/** Settings → Senders: sender domains and from-addresses (admin only to change). */
export function AfsenderePage() {
  const { isAdmin } = useAuth();
  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Senders</h1>
        <MockBadge />
      </header>
      {!isAdmin ? (
        <Alert>You have read-only access. Contact an administrator to change senders.</Alert>
      ) : null}
      <SendersSection isAdmin={isAdmin} />
      <DomainsSection isAdmin={isAdmin} />
      <SendingCostSection isAdmin={isAdmin} />
    </>
  );
}
